/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { ChannelConfiguration } from '@msdyn365-commerce/retail-proxy';

import { SearchURL, MapsURL, SubscriptionKeyCredential, Aborter } from 'azure-maps-rest';
import { IMapClient, ISuggestion } from '../interfaces';

export class AzureMapsClient implements IMapClient {
    private azureMapsSearchClient: SearchURL;
    private locale: string | undefined;
    private channel: ChannelConfiguration | undefined;
    private maxResults: number;
    private telemetry: Msdyn365.ITelemetry;
    private readonly searchTimeout = 10000;
    private readonly entityTypeMunicipality = 'Municipality';

    constructor(
        channel: ChannelConfiguration | undefined,
        telemetry: Msdyn365.ITelemetry,
        locale?: string,
        autoSuggestOptions?: Microsoft.Maps.IAutosuggestOptions
    ) {
        const pipeline = MapsURL.newPipeline(new SubscriptionKeyCredential(channel?.AzureMapsApiKey || ''));
        this.azureMapsSearchClient = new SearchURL(pipeline);
        this.locale = locale;
        this.channel = channel;
        this.telemetry = telemetry;
        this.maxResults = autoSuggestOptions?.maxResults || 5;
    }

    public initialize(isMapAPILoaded: boolean): void {
        // No initialization needed for Azure Maps
    }

    public async getSuggestions(query: string): Promise<ISuggestion[]> {
        return new Promise((resolve, reject) => {
            this.azureMapsSearchClient
                .searchFuzzy(Aborter.timeout(this.searchTimeout), query, {
                    limit: this.maxResults,
                    maxFuzzyLevel: 1,
                    language: this.locale || '',
                    typeahead: true,
                    entityType: [this.entityTypeMunicipality],
                    ...(this.channel?.ChannelCountryRegionISOCode ? { countrySet: [this.channel.ChannelCountryRegionISOCode] } : {})
                })
                .then(response => {
                    return resolve(
                        response.results?.map(result => ({
                            formattedSuggestion: result.address?.freeformAddress || '',
                            title: result.address?.freeformAddress || '',
                            subtitle: [result.address?.municipality, result.address?.countrySubdivisionName, result.address?.country]
                                .filter((part): part is string => !!part)
                                .join(', '),
                            latitude: result.position?.lat,
                            longitude: result.position?.lon
                        })) || []
                    );
                })
                .catch(error => {
                    this.telemetry.error('Error fetching suggestions from Azure Maps:', error);
                    console.log('Error fetching suggestions from Azure Maps:', error);
                    reject([]);
                });
        });
    }
}

export class BingMapsClient implements IMapClient {
    private autoSuggestManager: Microsoft.Maps.AutosuggestManager | undefined;
    private autoSuggestOptions?: Microsoft.Maps.IAutosuggestOptions;
    private channel: ChannelConfiguration | undefined;
    private telemetry: Msdyn365.ITelemetry;

    constructor(
        channel: ChannelConfiguration | undefined,
        telemetry: Msdyn365.ITelemetry,
        autoSuggestOptions?: Microsoft.Maps.IAutosuggestOptions
    ) {
        this.autoSuggestOptions = autoSuggestOptions;
        this.channel = channel;
        this.telemetry = telemetry;
    }

    public initialize(isMapAPILoaded: boolean): void {
        if (isMapAPILoaded) {
            this.initializeAutoSuggestManager();
        }
    }

    public async getSuggestions(query: string): Promise<ISuggestion[]> {
        return new Promise((resolve, reject) => {
            if (!this.autoSuggestManager) {
                reject('AutoSuggestManager is not initialized');
                return;
            }
            this.autoSuggestManager?.getSuggestions(query, (suggestions: Microsoft.Maps.ISuggestionResult[], query: string) => {
                resolve(
                    suggestions.map(suggestion => ({
                        formattedSuggestion: suggestion.formattedSuggestion,
                        title: suggestion.title,
                        subtitle: suggestion.subtitle
                    }))
                );
            });
        });
    }

    private initializeAutoSuggestManager(): void {
        if (!this.autoSuggestManager) {
            Microsoft.Maps.loadModule('Microsoft.Maps.AutoSuggest', {
                callback: () => {
                    const options = { ...this.autoSuggestOptions, countryCode: this.channel?.ChannelCountryRegionISOCode || '' };
                    this.autoSuggestManager = new Microsoft.Maps.AutosuggestManager(options);
                },
                errorCallback: () => {
                    this.telemetry.error('Unable to attach map auto suggest.');
                    console.error('Unable to attach map auto suggest.');
                },
                credentials: this.channel?.BingMapsApiKey || ''
            });
        }
    }
}

export class NoopMapClient implements IMapClient {
    constructor() {
        // Empty constructor
    }

    public initialize(isMapAPILoaded: boolean): void {
        // Do nothing - no initialization needed
    }

    public async getSuggestions(query: string): Promise<ISuggestion[]> {
        // Return empty suggestions list since auto-suggestion is disabled
        return Promise.resolve([]);
    }
}
